import { clamp, hexToRgb, luminance, mixRgb, smoothstep, type Rgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// Deep space on the voyage out: warm plum and wine beside the sun, cold
// midnight blue among the outer planets, then violet and magenta as the
// galaxy comes into view. `glow` tints the nebulae and the travelled trajectory.
const ZONES: Array<{ at: number; top: Rgb; bottom: Rgb; glow: Rgb }> = [
  { at: 0, top: hexToRgb('#140A26'), bottom: hexToRgb('#3B1328'), glow: hexToRgb('#FF9A5A') },
  { at: 0.35, top: hexToRgb('#070C26'), bottom: hexToRgb('#0C2140'), glow: hexToRgb('#4FD2FF') },
  { at: 0.7, top: hexToRgb('#0A0928'), bottom: hexToRgb('#1D0E46'), glow: hexToRgb('#8C6BFF') },
  { at: 1, top: hexToRgb('#12072F'), bottom: hexToRgb('#300B4A'), glow: hexToRgb('#FF6AD5') },
];

export interface CosmosSky {
  top: Rgb;
  bottom: Rgb;
  glow: Rgb;
}

/** The colour of space at a point on the voyage (0 beside the sun, 1 at the galaxy). */
export const cosmosSky = (progress: number): CosmosSky => {
  const distance = clamp(progress);
  let index = 0;
  while (index < ZONES.length - 2 && distance > ZONES[index + 1].at) {
    index += 1;
  }
  const from = ZONES[index];
  const to = ZONES[index + 1];
  const amount = smoothstep(from.at, to.at, distance);
  return {
    top: mixRgb(from.top, to.top, amount),
    bottom: mixRgb(from.bottom, to.bottom, amount),
    glow: mixRgb(from.glow, to.glow, amount),
  };
};

/** Space stays dark behind the header the whole way, so the header text stays white. */
export const cosmosChromeTone = (progress: number): ChromeTone => {
  const { top, bottom } = cosmosSky(progress);
  return luminance(top) * 0.8 + luminance(bottom) * 0.2 > 0.31 ? 'dark' : 'light';
};
